// Imports

import { useHistory } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import React, { useState } from "react"
import {
    Modal,
    Column,
    useIsLandscape,
    ErrorRotate,
    TitleSection,
    Label,
    useToken,
    updateMomentWithEditorTags,
    getProperty,
} from "trux"

import { PersonalizeMoment } from "trux/dist/organisms/PersonalizeMoment"
import { filterEventsWithPlaylist } from "../../util/playlistStore"
import { useFullVideoByMomentQuery } from "../../generated/graphql"
import { useSuspendedVideo } from "../../hooks/useSuspendedVideo"
import "./WrappedCreateMomentPage.css"

// Wrapped Create Moment Page

export const WrappedCreateMomentPage = (props) => {
    const history = useHistory()
    const dispatch = useDispatch()
    const isLandscapeMode = useIsLandscape()

    // Suspend the background video
    useSuspendedVideo(props.videoContainerRef)

    // loading
    const [loading, setLoading] = useState(false)

    // Token
    const token = useToken()
    const tokenReady = token !== null

    // Forwarding/success
    const [forwardingURL, setForwardingURL] = useState(null)
    const [newMoment, setNewMoment] = useState(null)
    const [success, setSuccess] = useState(false)
    const [showSuccess, setShowSuccess] = useState(false)
    const [finalUpdates, setFinalUpdates] = useState(null)

    // General
    const team = useSelector((state) => state.team.value)
    const game = useSelector((state) => state.game.value)
    const user = useSelector((state) => state.user.value)
    const playlists = game ? game.playlists : []
    const momentEventNumber = props.match.params.event_number
    const momentEventNumberInt = parseInt(props.match.params.event_number, 10)
    let isCoach = false
    if (user && team && team.members) {
        for (let member of team.members) {
            if (member.user_id === user.user_id) {
                // eslint-disable-next-line eqeqeq
                isCoach = member.is_coach == "1"
                break
            }
        }
    }

    //// Start and End Times
    let startVal = 0
    let pathParts = props.location.pathname.split("/")
    for (let part of pathParts) {
        if (part.includes("start=")) {
            startVal = parseInt(part.replace("start=", ""), 10)
            if (startVal !== startVal) {
                startVal = 0
            }
        }
    }

    // On close handler
    const handleOnClose = () => {
        // Remove the pure black class to the html element
        const element = document.querySelector("html")
        element.classList.remove("bg-pure-black-color-important")
        history.replace(
            props.location.pathname
                .replace("/create-moment", "")
                .replace("/options", "")
                .replace(/\/start.*$/gi, "")
        )
    }

    // Get the moment
    let moment = useSelector((state) => {
        let m = null
        const momentUrlParts = momentEventNumber.split(":")
        const eventCamera =
            momentUrlParts && momentUrlParts.length > 1
                ? momentUrlParts[1]
                : "tracecam"
        if (state.game.value && state.game.value.events) {
            for (let event of state.game.value.events) {
                if (event.event_number === momentEventNumberInt) {
                    if (
                        eventCamera !== "tracecam" &&
                        event.sources &&
                        event.sources.length > 0
                    ) {
                        for (let source of event.sources) {
                            if (
                                source.tags.find(
                                    (tag) => tag.name === eventCamera
                                )
                            ) {
                                m = Object.assign({}, event, source)
                            }
                        }
                    } else {
                        m = Object.assign({}, event)
                        break
                    }
                }
            }
            if (!m && momentEventNumber > 10000) {
                let mnum = momentEventNumberInt - 10000
                for (let event of state.game.value.events) {
                    if (event.event_number === mnum) {
                        if (
                            eventCamera !== "tracecam" &&
                            event.sources &&
                            event.sources.length > 0
                        ) {
                            for (let source of event.sources) {
                                if (
                                    source.tags.find(
                                        (tag) => tag.name === eventCamera
                                    )
                                ) {
                                    m = Object.assign({}, event, source)
                                }
                            }
                        } else {
                            m = Object.assign({}, event)
                            break
                        }
                    }
                }
            }
        }
        if (m && isCoach) {
            let ca = m.tags.find((t) => t.name === "coach-audio")
            if (!ca) {
                m.tags.push({ name: "coach-audio" })
            }
        }
        return m
    })

    // Get the full video moment
    const { data: fullVideoData } = useFullVideoByMomentQuery({
        variables: {
            game_clip_id: moment?.game_clip_id,
            token: token,
        },
        skip: !tokenReady || !moment,
    })
    const fullVideoMoment = fullVideoData
        ? fullVideoData.fullVideoByMoment
        : null

    // Half offset
    const halfOffset = fullVideoMoment
        ? (fullVideoMoment.time - fullVideoMoment.dynamic_start) * 1000
        : 0

    // If we don't have the moment, return
    if (!moment || !fullVideoMoment) {
        return null
    }

    // Pull some specific info from the moment
    let momentStart = fullVideoMoment.time
    let startOffset = fullVideoMoment.time

    // Get the playlists which the moment is a part of
    const getMomentPlaylists = () => {
        let result = []
        let reviewTags = finalUpdates.tags
        let sample = {
            tags: reviewTags
                .filter((t) => t.selected)
                .map((t) => {
                    return { name: t.name }
                }),
            meta: {
                thirds: reviewTags
                    .filter((t) => t.category === "Location" && t.selected)
                    .map((t) => t.third),
                trace_numbers: reviewTags
                    .filter((t) => t.category === "Player" && t.selected)
                    .map((t) => t.editor.attributes.meta.trace_numbers),
            },
        }
        for (const playlist of playlists) {
            const filteredEvents = filterEventsWithPlaylist(playlist, [sample])
            if (filteredEvents && filteredEvents.length > 0) {
                result = [...new Set([...result, playlist])]
            }
        }
        return result
    }
    const getPlayerTags = () => {
        let result = []
        let allTags = finalUpdates.tags
        for (const tag of allTags) {
            if (tag.category === "Players" && tag.selected) {
                result.push(tag)
            }
        }
        return result
    }

    // Our on save handlers
    const handleOnSave = async (updates) => {
        let updatedMoment = updateMomentWithEditorTags(moment, updates.tags)
        // users cannot create a full game moment
        updatedMoment.tags = updatedMoment.tags.filter(
            (t) => t.name !== "full-game"
        )
        // Playlist text
        let playlistText = ""
        let includedInPlaylists = []
        let reviewTags = updates.tags
        let sample = {
            tags: reviewTags
                .filter((t) => t.selected)
                .map((t) => {
                    return { name: t.name }
                }),
            meta: {
                thirds: reviewTags
                    .filter((t) => t.category === "Location" && t.selected)
                    .map((t) => t.third),
                trace_numbers: reviewTags
                    .filter((t) => t.category === "Player" && t.selected)
                    .map((t) => t.editor.attributes.meta.trace_numbers),
            },
        }
        if (game && game.playlists) {
            for (let playlist of game.playlists) {
                let prefix = playlistText.length === 0 ? "" : ", "
                let events = filterEventsWithPlaylist(playlist, [sample])
                if (playlist.enabled !== false && events.length > 0) {
                    playlistText += prefix + playlist.title
                    includedInPlaylists.push(playlist)
                }
            }
        }

        let utc = fullVideoMoment.start_time + updates.startAtRelative * 1000
        // Construct the body params
        let url = `https://lapi.traceup.com/tracebot-${process.env.REACT_APP_TRACE_ENV}/clips/create`
        let firstThird = getProperty(
            "meta.thirds.0",
            updatedMoment,
            "offensive"
        )
        let secondThird = getProperty(
            "meta.thirds.1",
            updatedMoment,
            "offensive"
        )
        let params = {
            folder: `api/teams/${team.name}/games/${team.name}-${game.game_id}`,
            video: fullVideoMoment.dynamic_hls,
            moment_start: momentStart,
            start: updates.startAtRelative * 1000,
            start_offset: startOffset * 1000,
            half_offset: halfOffset,
            duration: updates.stopAt - updates.startAt,
            game: game.game_id,
            team: team.team_id,
            team_name: team.name,
            half: fullVideoMoment.half,
            utc: utc,
            type: updatedMoment.meta.type,
            start_location: firstThird,
            end_location: secondThird,
            trace_numbers: updatedMoment.meta.trace_numbers,
            storage: game.webapp_storage_type,
            csp: updates.csp,
            editor_tags: updatedMoment.tags.map((t) => t.name),
            user_id: user.user_id,
            token: token,
            sport_type: game.sport_type,
        }

        setFinalUpdates(updates)

        // Make the call to create the moment
        setLoading(true)
        fetch(url, {
            method: "POST",
            body: JSON.stringify(params),
            mode: "cors",
        })
            .then((res) => {
                return res.json()
            })
            .then((json) => {
                if (json.error) {
                    alert(json.error)
                    setLoading(false)
                } else {
                    setLoading(false)
                    let playlistId = "all"
                    if (includedInPlaylists.length > 0) {
                        playlistId = includedInPlaylists[0].id
                    }
                    let url = `/${team.name}/games/${game.game_id}/${playlistId}/C${json.clip.game_clip_id}`
                    let finalMoment = json.clip
                    finalMoment["full_game_time"] = json.clip.time
                    finalMoment["tags"] = reviewTags
                        .filter((t) => t.selected)
                        .map((t) => {
                            return { name: t.name }
                        })
                    finalMoment["meta"] = {
                        ...finalMoment.meta,
                        thirds: reviewTags
                            .filter(
                                (t) => t.category === "Location" && t.selected
                            )
                            .map((t) => t.editor.attributes.meta.thirds),
                        trace_numbers: reviewTags
                            .filter(
                                (t) => t.category === "Player" && t.selected
                            )
                            .map((t) => t.editor.attributes.meta.trace_numbers),
                    }
                    setNewMoment(finalMoment)
                    setForwardingURL(url)
                    setSuccess(true)
                    setShowSuccess(true)
                }
            })
    }

    // Massage our moment for the PersonalizeMoment dialog
    let fullGameMoment = {
        base_path: fullVideoMoment.base_path,
        dynamic_audio: fullVideoMoment.dynamic_audio,
        dynamic_duration: fullVideoMoment.dynamic_duration,
        dynamic_hls: fullVideoMoment.dynamic_hls,
        dynamic_jfile: fullVideoMoment.dynamic_jfile,
        dynamic_offset: fullVideoMoment.dynamic_offset,
        dynamic_start: fullVideoMoment.dynamic_start,
        half: fullVideoMoment.half,
        game_clip_id: fullVideoMoment.game_clip_id,
        game_id: fullVideoMoment.game_id,
        team_id: fullVideoMoment.team_id,
        start_time: fullVideoMoment.start_time,
        tags: fullVideoMoment.tags,
        time: fullVideoMoment.time,
        meta: {
            type: fullVideoMoment.type,
            thirds: fullVideoMoment.thirds ? fullVideoMoment.thids : [],
            keywords: fullVideoMoment.keywords ? fullVideoMoment.keywords : [],
            trace_numbers: fullVideoMoment.trace_numbers,
            camera: { ...moment.camera },
        },
        sport_type: fullVideoMoment.sport_type
            ? fullVideoMoment.sport_type
            : game.sport_type,
    }

    const momentUTCTime = fullVideoMoment.start_time
    const massagedMomentStartTime = momentUTCTime + startVal * 1000
    let massagedMoment = {
        ...fullGameMoment,
        time: fullVideoMoment.time,
        start_time: massagedMomentStartTime,
        duration: 10,
        dynamic_duration: 10, // start with a 10s moment
        meta: {
            ...fullGameMoment.meta,
            thirds: ["offensive", "offensive"],
            type: "touch chain",
            types: ["touch chain"],
            keywords: ["touch chain"],
            trace_numbers: [],
        },
    }

    if (success) {
        // Determine which playlists the moment was added to and
        // then redirect to the "save-moment" flow
        const momentPlaylists = getMomentPlaylists()
        const playerTags = getPlayerTags()
        // Add the new events to redux game.value.events
        dispatch({
            type: "ADD_EVENT",
            value: newMoment,
        })
        setSuccess(false)
        // Push the new URL after a two second delay
        const finalURL = props.location.pathname.replace(
            "create-moment",
            "save-moment"
        )
        setTimeout(() => {
            history.push({
                pathname: finalURL,
                state: {
                    playerTags: playerTags,
                    playlists: momentPlaylists,
                    event: newMoment,
                    forceStandardModal: true,
                    momentToSave: newMoment,
                },
            })
        }, 2500)
        return <></>
    } else {
        return (
            <>
                {!isLandscapeMode && (
                    <Column
                        trkContext="Create Moment Page"
                        className="CreateMomentPage"
                    >
                        <PersonalizeMoment
                            loadingMessage="Creating game highlight..."
                            successMessage="Your game highlight was created!"
                            fullGameMoment={fullGameMoment}
                            onClose={handleOnClose}
                            onSave={handleOnSave}
                            moment={massagedMoment}
                            loading={loading}
                            success={showSuccess}
                            allowEditSpotlight={false}
                            allowConfirmation={true}
                            allowEditTitle={false}
                            tagsType="game"
                            game={game}
                            open={true}
                        />
                    </Column>
                )}
                {isLandscapeMode && (
                    <Modal open={true}>
                        <TitleSection
                            className="header"
                            onCloseClick={handleOnClose}
                        >
                            <Label
                                type={Label.Type.Heading4}
                                content="Create moment"
                            />
                        </TitleSection>
                        <ErrorRotate />
                    </Modal>
                )}
            </>
        )
    }
}
